import React, { useState, useEffect } from 'react';
import {
  Bell,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  Clock,
  Settings,
  Filter,
  Plus,
  Edit,
  Trash2,
  X,
  Save
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../lib/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';

const SystemNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { userProfile } = useAuth();

  // Form state for creating/editing notifications
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'info',
    priority: 1,
    target_roles: [],
    start_time: '',
    end_time: '',
    is_active: true
  });

  const notificationTypes = [
    { value: 'info', label: 'Information', icon: Info, color: 'text-blue-600 bg-blue-100' },
    { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'text-yellow-600 bg-yellow-100' },
    { value: 'error', label: 'Error', icon: XCircle, color: 'text-red-600 bg-red-100' },
    { value: 'maintenance', label: 'Maintenance', icon: Settings, color: 'text-purple-600 bg-purple-100' },
    { value: 'success', label: 'Success', icon: CheckCircle, color: 'text-green-600 bg-green-100' }
  ];

  const priorityLevels = [
    { value: 1, label: 'Low', color: 'bg-gray-100 text-gray-800' },
    { value: 2, label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 3, label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 4, label: 'Critical', color: 'bg-red-100 text-red-800' }
  ];

  const roles = ['admin', 'doctor', 'nurse', 'lab_technician', 'pharmacist', 'receptionist', 'billing_clerk', 'accountant', 'finance_manager', 'auditor'];

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'system_notifications'), orderBy('created_at', 'desc'));
      const snaps = await getDocs(q);
      const data = snaps.docs.map(d => ({ id: d.id, ...d.data() }));

      // Optionally fetch creator names if present
      const creatorIds = Array.from(new Set(data.map(n => n.created_by).filter(Boolean)));
      const creators = {};
      if (creatorIds.length > 0) {
        const profileSnaps = await getDocs(collection(db, 'user_profiles'));
        profileSnaps.docs.forEach(p => { const pd = p.data(); if (creatorIds.includes(pd.id)) creators[pd.id] = pd; });
      }

      const merged = data.map(n => ({ ...n, created_by_profile: creators[n.created_by] || null }));
      setNotifications(merged || []);
    } catch (error) {
      setError('Failed to load notifications: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotification = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      const notificationData = {
        ...formData,
        target_roles: formData?.target_roles?.length > 0 ? formData?.target_roles : null,
        start_time: formData?.start_time || null,
        end_time: formData?.end_time || null
      };

      await addDoc(collection(db, 'system_notifications'), notificationData);

      setSuccess('Notification created successfully!');
      setShowCreateModal(false);
      resetForm();
      loadNotifications();
    } catch (error) {
      setError('Failed to create notification: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotification = async (e) => {
    e?.preventDefault();
    if (!editingNotification) return;

    setLoading(true);
    setError('');

    try {
      const notificationData = {
        ...formData,
        target_roles: formData?.target_roles?.length > 0 ? formData?.target_roles : null,
        start_time: formData?.start_time || null,
        end_time: formData?.end_time || null
      };

      await updateDoc(doc(db, 'system_notifications', editingNotification?.id), notificationData);

      setSuccess('Notification updated successfully!');
      setEditingNotification(null);
      resetForm();
      loadNotifications();
    } catch (error) {
      setError('Failed to update notification: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!confirm('Are you sure you want to delete this notification?')) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'system_notifications', notificationId));

      setSuccess('Notification deleted successfully!');
      loadNotifications();
    } catch (error) {
      setError('Failed to delete notification: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleNotificationStatus = async (notification) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'system_notifications', notification?.id), { is_active: !notification?.is_active });

      loadNotifications();
    } catch (error) {
      setError('Failed to update notification status: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      notification_type: 'info',
      priority: 1,
      target_roles: [],
      start_time: '',
      end_time: '',
      is_active: true
    });
  };

  const openEditModal = (notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification?.title || '',
      message: notification?.message || '',
      notification_type: notification?.notification_type || 'info',
      priority: notification?.priority || 1,
      target_roles: notification?.target_roles || [],
      start_time: notification?.start_time ? new Date(notification?.start_time)?.toISOString()?.slice(0, 16) : '',
      end_time: notification?.end_time ? new Date(notification?.end_time)?.toISOString()?.slice(0, 16) : '',
      is_active: notification?.is_active ?? true
    });
  };

  const getNotificationIcon = (type) => {
    const notificationType = notificationTypes?.find(nt => nt?.value === type);
    const IconComponent = notificationType?.icon || Info;
    return <IconComponent className="h-5 w-5" />;
  };

  const getNotificationColor = (type) => {
    const notificationType = notificationTypes?.find(nt => nt?.value === type);
    return notificationType?.color || 'text-gray-600 bg-gray-100';
  };

  const getPriorityInfo = (priority) => {
    return priorityLevels?.find(p => p?.value === priority) || priorityLevels?.[0];
  };

  const isNotificationActive = (notification) => {
    if (!notification?.is_active) return false;

    const now = new Date();
    const startTime = notification?.start_time ? new Date(notification?.start_time) : null;
    const endTime = notification?.end_time ? new Date(notification?.end_time) : null;

    if (startTime && now < startTime) return false;
    if (endTime && now > endTime) return false;

    return true;
  };

  const filteredNotifications = notifications?.filter(notification => {
    const matchesType = filterType === 'all' || notification?.notification_type === filterType;
    const matchesPriority = filterPriority === 'all' || notification?.priority?.toString() === filterPriority;
    return matchesType && matchesPriority;
  });

  const activeNotifications = notifications?.filter(isNotificationActive)?.length || 0;
  const totalNotifications = notifications?.length || 0;

  const isAdmin = userProfile?.role === 'admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Notifications</h2>
          <p className="text-gray-600">Manage hospital-wide notifications and alerts</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Create Notification</span>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Notifications</p>
              <p className="text-2xl font-bold text-green-600">{activeNotifications}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Bell className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Notifications</p>
              <p className="text-2xl font-bold text-blue-600">{totalNotifications}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Settings className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Your Role</p>
              <p className="text-2xl font-bold text-gray-900 capitalize">{userProfile?.role?.replace('_', ' ')}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <CheckCircle className="h-6 w-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            {notificationTypes?.map(type => (
              <option key={type?.value} value={type?.value}>{type?.label}</option>
            ))}
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Priorities</option>
            {priorityLevels?.map(priority => (
              <option key={priority?.value} value={priority?.value}>{priority?.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm text-green-700">{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto">
              <X className="h-4 w-4 text-green-500" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading notifications...</p>
          </div>
        ) : filteredNotifications?.length === 0 ? (
          <div className="bg-white p-8 rounded-lg border border-gray-200 text-center text-gray-500">
            No notifications found
          </div>
        ) : (
          filteredNotifications?.map((notification) => {
            const priorityInfo = getPriorityInfo(notification?.priority);
            const isActive = isNotificationActive(notification);

            return (
              <div
                key={notification?.id}
                className={`bg-white rounded-lg border-l-4 border border-gray-200 p-6 ${isActive ? 'border-l-blue-500' : 'border-l-gray-300 opacity-75'
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-2 rounded-full ${getNotificationColor(notification?.notification_type)}`}>
                      {getNotificationIcon(notification?.notification_type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">{notification?.title}</h3>

                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityInfo?.color}`}>
                          {priorityInfo?.label} Priority
                        </span>

                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <p className="text-gray-600 mb-3">{notification?.message}</p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>Created: {(() => { const d = new Date(notification?.created_at); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; })()}</span>
                        </div>

                        {notification?.target_roles && (
                          <div>
                            <span className="font-medium">Target Roles:</span> {notification?.target_roles?.join(', ')}
                          </div>
                        )}

                        {notification?.user_profiles?.full_name && (
                          <div>
                            <span className="font-medium">Created by:</span> {notification?.user_profiles?.full_name}
                          </div>
                        )}

                        {notification?.start_time && (
                          <div>
                            <span className="font-medium">Start:</span> {(() => { const d = new Date(notification?.start_time); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()}
                          </div>
                        )}

                        {notification?.end_time && (
                          <div>
                            <span className="font-medium">End:</span> {(() => { const d = new Date(notification?.end_time); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => toggleNotificationStatus(notification)}
                        className={`p-2 rounded-lg transition-colors ${notification?.is_active
                            ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'
                          }`}
                        title={notification?.is_active ? 'Deactivate' : 'Activate'}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => openEditModal(notification)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit notification"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDeleteNotification(notification?.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete notification"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create/Edit Notification Modal */}
      {(showCreateModal || editingNotification) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingNotification ? 'Edit Notification' : 'Create Notification'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingNotification(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <form onSubmit={editingNotification ? handleUpdateNotification : handleCreateNotification} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData?.title}
                    onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    value={formData?.message}
                    onChange={(e) => setFormData({ ...formData, message: e?.target?.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type *
                  </label>
                  <select
                    value={formData?.notification_type}
                    onChange={(e) => setFormData({ ...formData, notification_type: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {notificationTypes?.map(type => (
                      <option key={type?.value} value={type?.value}>{type?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority *
                  </label>
                  <select
                    value={formData?.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e?.target?.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {priorityLevels?.map(priority => (
                      <option key={priority?.value} value={priority?.value}>{priority?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData?.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData?.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Roles (Leave empty for all roles)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {roles?.map(role => (
                      <label key={role} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData?.target_roles?.includes(role)}
                          onChange={(e) => {
                            if (e?.target?.checked) {
                              setFormData({
                                ...formData,
                                target_roles: [...formData?.target_roles, role]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                target_roles: formData?.target_roles?.filter(r => r !== role)
                              });
                            }
                          }}
                          className="h-4 w-4 text-blue-600"
                        />
                        <span className="text-sm capitalize">{role?.replace('_', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData?.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e?.target?.checked })}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingNotification(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="h-4 w-4" />
                  <span>{loading ? 'Saving...' : editingNotification ? 'Update' : 'Create'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemNotifications;